import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, FileText, Pencil, Trash2, Save,
  CheckCircle, XCircle, Filter, ChevronUp, ChevronDown,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PurchaseContract {
  id: string;
  number: string;
  vendorId: string;
  vendorName?: string;
  vendor?: { id: string; name: string };
  title: string;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  validFrom: string;
  validTo: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  autoPo: boolean;
  items?: ContractItem[];
  _count?: { items?: number };
  createdAt?: string;
}

interface ContractItem {
  description: string;
  productId?: string;
  rate: number;
  minQty?: number;
  maxQty?: number;
  unit: string;
}

interface Vendor {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-50 text-green-700',
  expired: 'bg-red-50 text-red-700',
  terminated: 'bg-orange-50 text-orange-700',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
};

const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Advance', 'COD'];

// ─── Main Component ───────────────────────────────────────────────────────────

export function PurchaseContractsPage() {
  const [contracts, setContracts] = useState<PurchaseContract[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Create Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendorId: '', title: '', validFrom: '', validTo: '',
    paymentTerms: 'Net 30', deliveryTerms: '', autoPo: false,
  });
  const [items, setItems] = useState<ContractItem[]>([
    { description: '', productId: '', rate: 0, minQty: undefined, maxQty: undefined, unit: 'pcs' },
  ]);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '', vendorId: '', title: '', validFrom: '', validTo: '',
    paymentTerms: 'Net 30', deliveryTerms: '', autoPo: false,
  });
  const [editItems, setEditItems] = useState<ContractItem[]>([]);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/contracts/purchase-contracts', { params });
      setContracts(res.data.data?.purchaseContracts ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load purchase contracts');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, sortField, sortDir, page]);

  useEffect(() => {
    fetchContracts();
    api.get('/vendors').then(res => setVendors(res.data.data?.vendors ?? res.data.data ?? [])).catch(() => {});
    api.get('/products').then(res => setProducts(res.data.data?.products ?? res.data.data ?? [])).catch(() => {});
  }, [fetchContracts]);

  const handleCreate = async () => {
    const validItems = items.filter(i => i.description.trim());
    if (validItems.length === 0) {
      alert('Please add at least one item');
      return;
    }
    try {
      setSaving(true);
      await api.post('/contracts/purchase-contracts', {
        vendorId: form.vendorId,
        title: form.title,
        validFrom: form.validFrom,
        validTo: form.validTo,
        paymentTerms: form.paymentTerms || undefined,
        deliveryTerms: form.deliveryTerms || undefined,
        autoPo: form.autoPo,
        items: validItems.map(i => ({
          description: i.description,
          productId: i.productId || undefined,
          rate: Number(i.rate),
          minQty: i.minQty ? Number(i.minQty) : undefined,
          maxQty: i.maxQty ? Number(i.maxQty) : undefined,
          unit: i.unit,
        })),
      });
      setShowModal(false);
      setForm({ vendorId: '', title: '', validFrom: '', validTo: '', paymentTerms: 'Net 30', deliveryTerms: '', autoPo: false });
      setItems([{ description: '', productId: '', rate: 0, minQty: undefined, maxQty: undefined, unit: 'pcs' }]);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create contract');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (c: PurchaseContract) => {
    if (c.status !== 'draft') {
      alert('Only draft contracts can be edited');
      return;
    }
    setEditForm({
      id: c.id,
      vendorId: c.vendorId || c.vendor?.id || '',
      title: c.title,
      validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
      validTo: c.validTo ? c.validTo.split('T')[0] : '',
      paymentTerms: c.paymentTerms || 'Net 30',
      deliveryTerms: c.deliveryTerms || '',
      autoPo: c.autoPo,
    });
    setEditItems(c.items && c.items.length > 0
      ? c.items.map(i => ({ ...i }))
      : [{ description: '', productId: '', rate: 0, minQty: undefined, maxQty: undefined, unit: 'pcs' }]
    );
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    const validItems = editItems.filter(i => i.description.trim());
    try {
      setEditSaving(true);
      await api.put(`/contracts/purchase-contracts/${editForm.id}`, {
        vendorId: editForm.vendorId,
        title: editForm.title,
        validFrom: editForm.validFrom,
        validTo: editForm.validTo,
        paymentTerms: editForm.paymentTerms || undefined,
        deliveryTerms: editForm.deliveryTerms || undefined,
        autoPo: editForm.autoPo,
        items: validItems.map(i => ({
          description: i.description,
          productId: i.productId || undefined,
          rate: Number(i.rate),
          minQty: i.minQty ? Number(i.minQty) : undefined,
          maxQty: i.maxQty ? Number(i.maxQty) : undefined,
          unit: i.unit,
        })),
      });
      setShowEditModal(false);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update contract');
    } finally {
      setEditSaving(false);
    }
  };

  const handleActivate = async (c: PurchaseContract) => {
    if (c.status !== 'draft') return;
    if (!confirm(`Activate contract "${c.title}"? This will make it active and immutable.`)) return;
    try {
      await api.patch(`/contracts/purchase-contracts/${c.id}/activate`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to activate contract');
    }
  };

  const handleTerminate = async (c: PurchaseContract) => {
    if (c.status !== 'active') return;
    if (!confirm(`Terminate contract "${c.title}"? This action cannot be undone.`)) return;
    try {
      await api.patch(`/contracts/purchase-contracts/${c.id}/terminate`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to terminate contract');
    }
  };

  const handleDelete = async (c: PurchaseContract) => {
    if (c.status !== 'draft') {
      alert('Only draft contracts can be deleted');
      return;
    }
    if (!confirm(`Delete contract "${c.title}"?`)) return;
    try {
      await api.delete(`/contracts/purchase-contracts/${c.id}`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete contract');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const addItem = (target: 'create' | 'edit') => {
    const newItem: ContractItem = { description: '', productId: '', rate: 0, minQty: undefined, maxQty: undefined, unit: 'pcs' };
    if (target === 'create') setItems([...items, newItem]);
    else setEditItems([...editItems, newItem]);
  };

  const removeItem = (idx: number, target: 'create' | 'edit') => {
    if (target === 'create') {
      if (items.length <= 1) return;
      setItems(items.filter((_, i) => i !== idx));
    } else {
      if (editItems.length <= 1) return;
      setEditItems(editItems.filter((_, i) => i !== idx));
    }
  };

  const updateItem = (idx: number, field: keyof ContractItem, value: any, target: 'create' | 'edit') => {
    if (target === 'create') {
      const updated = [...items];
      updated[idx] = { ...updated[idx], [field]: value };
      setItems(updated);
    } else {
      const updated = [...editItems];
      updated[idx] = { ...updated[idx], [field]: value };
      setEditItems(updated);
    }
  };

  // Render items editor
  const renderItemsEditor = (itemList: ContractItem[], target: 'create' | 'edit') => (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Contract Items *</label>
        <button onClick={() => addItem(target)} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
          <Plus size={12} /> Add Item
        </button>
      </div>
      <div className="space-y-2 rounded-lg border border-gray-200 p-3 max-h-[300px] overflow-y-auto">
        {itemList.map((item, idx) => (
          <div key={idx} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input type="text" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value, target)}
                placeholder="Description" className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              {itemList.length > 1 && (
                <button onClick={() => removeItem(idx, target)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><X size={14} /></button>
              )}
            </div>
            <div className="grid grid-cols-5 gap-2">
              <select value={item.productId || ''} onChange={(e) => updateItem(idx, 'productId', e.target.value, target)}
                className="col-span-2 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none">
                <option value="">No Product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="number" value={item.rate || ''} onChange={(e) => updateItem(idx, 'rate', e.target.value, target)}
                placeholder="Rate" min="0" step="0.01" className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              <input type="number" value={item.minQty || ''} onChange={(e) => updateItem(idx, 'minQty', e.target.value, target)}
                placeholder="Min Qty" min="0" className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              <input type="number" value={item.maxQty || ''} onChange={(e) => updateItem(idx, 'maxQty', e.target.value, target)}
                placeholder="Max Qty" min="0" className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            </div>
            <input type="text" value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value, target)}
              placeholder="Unit (pcs, kg, etc.)" className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Contracts</h1>
          <p className="text-sm text-gray-500">Manage vendor agreements and contract terms</p>
        </div>
        <button
          onClick={() => { setItems([{ description: '', productId: '', rate: 0, minQty: undefined, maxQty: undefined, unit: 'pcs' }]); setShowModal(true); }}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Contract
        </button>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search contracts..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
          <Filter size={16} /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
            <option value="">All Statuses</option>
            {Object.keys(STATUS_LABELS).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          {filterStatus && (
            <button onClick={() => setFilterStatus('')} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading contracts...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchContracts} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText size={40} className="mb-2" />
            <p className="text-sm">No purchase contracts found</p>
            <p className="text-xs">Create a contract to define vendor agreements</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('number')}>
                    <span className="flex items-center gap-1">Number <SortIcon field="number" /></span>
                  </th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validFrom')}>
                    <span className="flex items-center gap-1">Valid From <SortIcon field="validFrom" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validTo')}>
                    <span className="flex items-center gap-1">Valid To <SortIcon field="validTo" /></span>
                  </th>
                  <th className="px-4 py-3 text-center">Items</th>
                  <th className="px-4 py-3 text-center">Auto-PO</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contracts.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-medium text-gray-900">{c.number}</td>
                    <td className="px-4 py-3 text-gray-900">{c.vendorName || c.vendor?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{c.title}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(c.validFrom)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(c.validTo)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{c._count?.items ?? c.items?.length ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      {c.autoPo ? (
                        <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">Yes</span>
                      ) : (
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {c.status === 'draft' && (
                          <>
                            <button onClick={() => handleActivate(c)}
                              className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors" title="Activate">
                              <CheckCircle size={12} /> Activate
                            </button>
                            <button onClick={() => openEditModal(c)} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                            <button onClick={() => handleDelete(c)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                          </>
                        )}
                        {c.status === 'active' && (
                          <button onClick={() => handleTerminate(c)}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-colors" title="Terminate">
                            <XCircle size={12} /> Terminate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Create Contract Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Create Purchase Contract</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vendor *</label>
                <select value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Annual Steel Supply Agreement"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valid From *</label>
                  <input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valid To *</label>
                  <input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Payment Terms</label>
                  <select value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Delivery Terms</label>
                  <input type="text" value={form.deliveryTerms} onChange={(e) => setForm({ ...form, deliveryTerms: e.target.value })}
                    placeholder="e.g. Ex-Works, CIF"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="autoPo" checked={form.autoPo} onChange={(e) => setForm({ ...form, autoPo: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                <label htmlFor="autoPo" className="text-sm font-medium text-gray-700">Auto-generate POs from this contract</label>
              </div>
              {renderItemsEditor(items, 'create')}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreate}
                disabled={saving || !form.vendorId || !form.title || !form.validFrom || !form.validTo}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contract Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowEditModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Purchase Contract</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vendor *</label>
                <select value={editForm.vendorId} onChange={(e) => setEditForm({ ...editForm, vendorId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valid From *</label>
                  <input type="date" value={editForm.validFrom} onChange={(e) => setEditForm({ ...editForm, validFrom: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valid To *</label>
                  <input type="date" value={editForm.validTo} onChange={(e) => setEditForm({ ...editForm, validTo: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Payment Terms</label>
                  <select value={editForm.paymentTerms} onChange={(e) => setEditForm({ ...editForm, paymentTerms: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Delivery Terms</label>
                  <input type="text" value={editForm.deliveryTerms} onChange={(e) => setEditForm({ ...editForm, deliveryTerms: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="editAutoPo" checked={editForm.autoPo} onChange={(e) => setEditForm({ ...editForm, autoPo: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                <label htmlFor="editAutoPo" className="text-sm font-medium text-gray-700">Auto-generate POs from this contract</label>
              </div>
              {renderItemsEditor(editItems, 'edit')}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowEditModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleEdit}
                disabled={editSaving || !editForm.vendorId || !editForm.title || !editForm.validFrom || !editForm.validTo}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
